// Idukki plans. The single source of truth for what /pricing sells:
// the plan cards, the comparison-table headers, the SoftwareApplication
// offers JSON-LD and the "which Idukki band covers this spend" line on the
// competitor pricing pages all read from this array. Numbers come from the
// official internal pricing sheet (USD, billed monthly; annual = 20% off).
// Update here if the sheet moves.

export type PlanKey = 'free' | 'starter' | 'growth' | 'professional' | 'enterprise'

export type Plan = {
  key: PlanKey
  name: string
  badge: string
  priceMonthly: string
  priceAnnual: string
  /** Numeric monthly price in USD; null for the custom-priced tier. */
  monthlyUsd: number | null
  /** Upper bound of the monthly widget-impression band; null = custom. */
  impressionCap: number | null
  impressions: string
  stores: string
  summary: string
  cta: string
  href: string
  featured?: boolean
}

export const BOOK_DEMO_HREF = 'https://app.apollo.io/#/meet/idukki/30-min-1'
export const REGISTER_HREF = 'https://app.idukki.io/register'

export const PLANS: Plan[] = [
  {
    key: 'free',
    // Named "Free" so the plan name and its "Free forever" badge tell the
    // same story (the old "Startup" label contradicted the badge).
    name: 'Free',
    badge: 'Free forever',
    priceMonthly: '$0',
    priceAnnual: '$0',
    monthlyUsd: 0,
    impressionCap: 5000,
    impressions: 'Up to 5,000 / mo',
    stores: '1 store',
    summary: 'Launch with image-only UGC widgets, AI moderation and every standard integration. Idukki logo stays on widgets.',
    cta: 'Start free',
    href: REGISTER_HREF,
  },
  {
    key: 'starter',
    name: 'Starter',
    badge: 'Plan A',
    priceMonthly: '$45',
    priceAnnual: '$432',
    monthlyUsd: 45,
    impressionCap: 10000,
    impressions: 'Up to 10,000 / mo',
    stores: '1 store',
    summary: 'Full shoppable video, rights management, white-labelled widgets, hands-on onboarding and a success manager.',
    cta: 'Start free',
    href: REGISTER_HREF,
  },
  {
    key: 'growth',
    name: 'Growth',
    badge: 'Most popular',
    priceMonthly: '$75',
    priceAnnual: '$720',
    monthlyUsd: 75,
    impressionCap: 20000,
    impressions: 'Up to 20,000 / mo',
    stores: '2 stores',
    summary: 'For scaling DTC teams running multiple stores or markets. Daily reporting, every premium widget layout, full AI stack.',
    cta: 'Start free',
    href: REGISTER_HREF,
    featured: true,
  },
  {
    key: 'professional',
    name: 'Professional',
    badge: 'Plan C',
    priceMonthly: '$200',
    priceAnnual: '$1,920',
    monthlyUsd: 200,
    impressionCap: 50000,
    impressions: 'Up to 50,000 / mo',
    stores: '5 stores',
    summary: 'For brand portfolios, agencies + multi-region operators. Includes performance review cadence and priority support.',
    cta: 'Book a demo',
    href: BOOK_DEMO_HREF,
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    badge: 'Plan D',
    priceMonthly: 'Custom',
    priceAnnual: 'Custom',
    monthlyUsd: null,
    impressionCap: null,
    impressions: 'Above 50,000 / mo',
    stores: '10+ stores',
    summary: 'Custom impression bands, custom contract terms, SLA, dedicated infra. Sold direct.',
    cta: 'Talk to sales',
    href: '/contact?intent=enterprise',
  },
]

export const pricedPlans = PLANS.filter((p): p is Plan & { monthlyUsd: number } => p.monthlyUsd !== null)

/** schema.org Offer[] derived from the real plan list (priced tiers only). */
export function planOffers(pricingUrl: string) {
  return pricedPlans.map((p) => ({
    '@type': 'Offer',
    name: `Idukki ${p.name}`,
    price: String(p.monthlyUsd),
    priceCurrency: 'USD',
    url: pricingUrl,
    description: `${p.impressions} widget impressions, ${p.stores}.`,
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price: String(p.monthlyUsd),
      priceCurrency: 'USD',
      billingIncrement: 1,
      unitCode: 'MON',
    },
  }))
}

/** schema.org AggregateOffer derived from the real plan list. */
export function planAggregateOffer(pricingUrl: string) {
  const prices = pricedPlans.map((p) => p.monthlyUsd)
  return {
    '@type': 'AggregateOffer',
    priceCurrency: 'USD',
    lowPrice: String(Math.min(...prices)),
    highPrice: String(Math.max(...prices)),
    offerCount: String(pricedPlans.length),
    url: pricingUrl,
    offers: planOffers(pricingUrl),
  }
}

/**
 * The cheapest Idukki plan whose impression band covers a given monthly
 * widget-impression volume. Returns the Enterprise tier above 50K.
 */
export function planForImpressions(monthlyImpressions: number): Plan {
  return (
    PLANS.find((p) => p.impressionCap !== null && monthlyImpressions <= p.impressionCap) ??
    PLANS[PLANS.length - 1]
  )
}

/** The cheapest paid Idukki plan at or under a monthly spend, if any. */
export function plansAtOrUnder(monthlyUsd: number): Plan[] {
  return pricedPlans.filter((p) => p.monthlyUsd <= monthlyUsd)
}
